import type { HearingParticipantStatus } from 'features/call/call-hearing/interfaces';
import { EmergencyConnectionPoint } from 'features/emergency/enums';
import { VoiceSessionStatus } from 'features/voice-session/enums';

export type EmergencyCalleeButtonState =
  | 'hidden'
  | 'voice-session-cancel'
  | 'hearing-cancel'
  | 'reconnect'
  | 'disconnect'
  | 'connect'
  | 'none';

interface ResolveEmergencyCalleeButtonStateArgs {
  shouldCallControlsBeVisible: boolean;
  voiceSessionStatus: VoiceSessionStatus;
  hearingStatus: HearingParticipantStatus;
  isCalledToConnectionPoint: boolean;
  isEmergencyCalleeReconnect: boolean;
}

export const resolveEmergencyCalleeButtonState = ({
  shouldCallControlsBeVisible,
  voiceSessionStatus,
  hearingStatus,
  isCalledToConnectionPoint,
  isEmergencyCalleeReconnect,
}: ResolveEmergencyCalleeButtonStateArgs): EmergencyCalleeButtonState => {
  if (!shouldCallControlsBeVisible) {
    return 'hidden';
  }

  if (voiceSessionStatus === VoiceSessionStatus.CONNECTING) {
    return 'voice-session-cancel';
  }

  if (hearingStatus === 'connecting') {
    return 'hearing-cancel';
  }

  if (isCalledToConnectionPoint && isEmergencyCalleeReconnect) {
    return 'reconnect';
  }

  if (hearingStatus === 'connected') {
    return 'disconnect';
  }

  if (!isCalledToConnectionPoint || hearingStatus === 'disconnected') {
    return 'connect';
  }

  return 'none';
};

export type EmergencyCalleePresentation = 'ecrc' | 'psap' | 'other' | 'none';

interface ResolveEmergencyCalleePresentationArgs {
  emergencyConnectionPoint?: EmergencyConnectionPoint;
  isOtherConnectionPoint: boolean;
  isEmergencyCalleeReconnect: boolean;
  hearingStatus: HearingParticipantStatus;
  isCalledToConnectionPoint: boolean;
}

export const resolveEmergencyCalleePresentation = ({
  emergencyConnectionPoint,
  isOtherConnectionPoint,
  isEmergencyCalleeReconnect,
  hearingStatus,
  isCalledToConnectionPoint,
}: ResolveEmergencyCalleePresentationArgs): EmergencyCalleePresentation => {
  if (emergencyConnectionPoint === EmergencyConnectionPoint.ECRC) {
    return 'ecrc';
  }

  if (emergencyConnectionPoint === EmergencyConnectionPoint.PSAP) {
    return 'psap';
  }

  if (
    isOtherConnectionPoint &&
    (!isEmergencyCalleeReconnect ||
      hearingStatus === 'connecting' ||
      isCalledToConnectionPoint)
  ) {
    return 'other';
  }

  return 'none';
};
